import { useTranslation } from 'react-i18next';
import type { SignatureDocument, SignatureStatus } from '@worki/shared';
import { SIGNATURE_STATUSES } from '@worki/shared';
import { currentLocale } from '../../i18n.js';
import { formatDateTime } from '../../lib/format.js';
import { sendActionOf } from '../../lib/sendAction.js';
import { Select } from '../../components/Select.js';
import { DataTable, type Column } from '../../components/DataTable.js';
import { Icon } from '../../components/Icon.js';
import { ItemActions } from '../../components/ItemActions.js';
import { documentNumber, recipientLine, StatusBadge } from './parts.js';

/**
 * SIG-004 — the documents, ordered by what has to be done next.
 *
 * A document nobody has opened is a different problem from one that was read
 * and left unsigned, so views are shown wherever a signature is not: they are
 * the only thing separating "not got round to it" from "never arrived", which
 * is what decides whether to chase or to resend.
 */
export function DocumentList({
  rows,
  loading,
  status,
  onStatusChange,
  page,
  pageSize,
  total,
  onPageChange,
  onOpen,
  onResend,
  onDelete,
}: {
  rows: SignatureDocument[];
  loading: boolean;
  status: SignatureStatus | '';
  onStatusChange: (value: SignatureStatus | '') => void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onOpen: (document: SignatureDocument) => void;
  onResend: (document: SignatureDocument) => void;
  onDelete: (document: SignatureDocument) => void;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();

  const columns: Array<Column<SignatureDocument>> = [
    {
      key: 'title',
      header: t('signing.document'),
      render: (row) => (
        <div>
          <strong>{row.title}</strong>
          {/* Who it went to, under the name of the thing sent, after the number
              it is referred to by: the three together are how a document is
              recognised weeks later, and the recipient is what the search box
              looks through. */}
          <div className="list-row__meta">
            <span className="ltr-nums">{t('signing.numberBadge', { number: documentNumber(row) })}</span>
            {' · '}
            {recipientLine(row)}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('signing.statusLabel'),
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'signer',
      header: t('signing.signer'),
      render: (row) =>
        row.signedAt ? (
          <div>
            <strong>{row.signerName}</strong>
            <div className="list-row__meta ltr-nums">{formatDateTime(row.signedAt, locale)}</div>
          </div>
        ) : (
          <span className="muted">
            {row.viewCount > 0 ? t('signing.viewedTimes', { count: row.viewCount }) : t('signing.notViewed')}
          </span>
        ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (row) => <RowActions row={row} onOpen={onOpen} onResend={onResend} onDelete={onDelete} />,
    },
  ];

  return (
    <DataTable
      rows={rows}
      columns={columns}
      rowKey={(row) => row.id}
      loading={loading}
      onRowClick={onOpen}
      card={(row) => (
        <>
          <div className="list-card__head">
            <span className="list-card__title">
              <Icon name="document" size={17} />
              {row.title}
            </span>
            <StatusBadge status={row.status} />
          </div>
          <p className="list-card__sub">
            <span className="ltr-nums">{t('signing.numberBadge', { number: documentNumber(row) })}</span>
            {' · '}
            {recipientLine(row)}
          </p>
          <div className="list-card__foot">
            <span className="list-card__meta ltr-nums">
              {formatDateTime(row.signedAt ?? row.createdAt, locale)}
            </span>
            <span className="list-card__actions">
              <RowActions row={row} onOpen={onOpen} onResend={onResend} onDelete={onDelete} />
            </span>
          </div>
        </>
      )}
      page={page}
      pageSize={pageSize}
      total={total}
      onPageChange={onPageChange}
      filters={
        <Select
          value={status}
          aria-label={t('signing.statusLabel')}
          onChange={(value) => onStatusChange(value as SignatureStatus | '')}
        >
          <option value="">{t('common.allOf', { label: t('signing.statusLabel') })}</option>
          {SIGNATURE_STATUSES.map((value) => (
            <option key={value} value={value}>
              {t(`signing.status.${value}`)}
            </option>
          ))}
        </Select>
      }
      empty={{
        icon: <Icon name="document" size={30} />,
        title: t('signing.emptyTitle'),
        body: t('signing.emptyBody'),
      }}
    />
  );
}

/**
 * Two digits, so a list of them lines up and reads as a series.
 *
 * Rows made before the number existed carry 0, which is not a number anybody
 * gave them -- they show a dash rather than "00", because a document numbered
 * zero would be a document somebody has to ask about.
 */
/**
 * The row's own menu, in the table and on the card.
 *
 * Opening the document to chase it was three taps and a panel that loads the
 * file: the two things anybody does from a list — send it again, throw it away
 * — are done from the row. Sending again carries its tally, because "sent" and
 * "sent four times" call for different next moves.
 *
 * A signed document is finished, so there is nothing left to ask for: the send
 * on its row is a copy of what was signed. A refused, withdrawn or lapsed one is
 * offered no send at all. Which is which is the one rule every screen that
 * sends asks, so this menu cannot drift from the history list's.
 */
function RowActions({
  row,
  onOpen,
  onResend,
  onDelete,
}: {
  row: SignatureDocument;
  onOpen: (document: SignatureDocument) => void;
  onResend: (document: SignatureDocument) => void;
  onDelete: (document: SignatureDocument) => void;
}) {
  const { t } = useTranslation();
  const send = sendActionOf('document', row);

  return (
    <ItemActions
      onView={() => onOpen(row)}
      viewLabel={t('common.view')}
      {...(send
        ? {
            onResend: () => onResend(row),
            resendCount: row.sendCount,
            // A draft has never gone out, so its menu offers a send, not a resend.
            ...(send === 'send' ? { resendLabel: t('common.send') } : {}),
            ...(send === 'copy' ? { resendLabel: t('history.sendCopy') } : {}),
          }
        : {})}
      onDelete={() => onDelete(row)}
    />
  );
}
